/**
 * Reads/writes the `locale` cookie. Once set, the browser sends it on every
 * request to a host that matches the cookie's `Domain` attribute, provided
 * the calling axios/fetch uses `withCredentials: true` and the API CORS
 * response allows credentials.
 *
 * The cookie `Domain` is the registrable domain (eTLD+1) derived from
 * `window.location.hostname`, so API hosts that are siblings of the storefront
 * (e.g. `api.benedek.ro` vs `benedek.ro`, or `bndk-api-stage….` vs `bndk-stage….`
 * under the same site) receive the same cookie. For `localhost` and loopback
 * we omit `Domain` (host-only cookie). Cookie lifetime is ~30 days (`max-age`).
 *
 * Storefront routing uses this cookie (when readable via `document.cookie`) as the primary
 * language preference: URLs are aligned to it rather than the other way around. An HttpOnly
 * `locale` cookie cannot drive client redirects — avoid HttpOnly on this name for storefront flows.
 */

import { getRegistrableCookieDomainFromHostname } from './registrable-cookie-domain';

export const LOCALE_COOKIE_NAME = 'locale';

/** ~30 days — balances remembering preference vs stale locale after long absence. */
export const LOCALE_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

const resolveLocaleCookieDomain = (): string | undefined => {
  if (typeof location === 'undefined') return undefined;
  const host = location.hostname;
  if (!host) return undefined;
  return getRegistrableCookieDomainFromHostname(host);
};

export const getLocaleCookie = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

export const setLocaleCookie = (locale: string | undefined | null): void => {
  if (typeof document === 'undefined') return;
  const value = (locale ?? '').trim();
  if (!value) return;

  const domain = resolveLocaleCookieDomain();
  const isHttps = location.protocol === 'https:';

  const parts = [
    `${LOCALE_COOKIE_NAME}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (domain) parts.push(`domain=${domain}`);
  if (isHttps) parts.push('Secure');

  document.cookie = parts.join('; ');
};

/** Deletes the locale cookie (used to roll back an optimistic write). */
export const clearLocaleCookie = (): void => {
  if (typeof document === 'undefined') return;

  const domain = resolveLocaleCookieDomain();
  const parts = [`${LOCALE_COOKIE_NAME}=`, 'path=/', 'max-age=0', 'SameSite=Lax'];
  if (domain) parts.push(`domain=${domain}`);

  document.cookie = parts.join('; ');
};
